import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Edge, Node } from '../../../core/models/scene';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';
import { ApplyGenogramAutoLayoutCommand } from '../commands/ApplyGenogramAutoLayoutCommand';

const PERSON_SIZE = 60;
const SPACING = 120;

const getNodeSize = (node: Node): { width: number; height: number } => ({
  width: Number.isFinite(node.w) ? node.w! : node.width ?? PERSON_SIZE,
  height: Number.isFinite(node.h) ? node.h! : node.height ?? PERSON_SIZE,
});

// Adds two parents (male + female), connects them with a marriage edge,
// and links both as parents of the selected person.
export class AddParentsTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-add-parents',
    name: i18n.t('genogram:tools.addParents', { defaultValue: 'Add Parents' }) as string,
    icon: '👪',
    category: 'create',
    description: 'Add two parents to the selected person (married pair)',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false,
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: false,
      supportsBatchOperations: false,
    },
    shortcuts: [
      { key: '4', description: 'Add parents (when one person selected)' },
    ],
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.addParents', 'Add Parents');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    const summary = context.selectionSummary;
    return summary?.contextType === 'person';
  }

  execute(): ICommand | null {
    if (!this.context) return null;

    const selectedId = Array.from(this.context.selection)[0];
    const state = useDiagramStore.getState();
    const selectedNode = state.nodes.find((n) => n.id === selectedId);
    if (!selectedNode) return null;

    const size = getNodeSize(selectedNode);

    // Place parents above the selected person, centered as a couple
    const childCenterX = (selectedNode.x ?? 0) + size.width / 2;
    const parentY = (selectedNode.y ?? 0) - size.height - SPACING;
    const leftParentX = Math.round(childCenterX - SPACING / 2 - size.width);
    const rightParentX = Math.round(childCenterX + SPACING / 2);

    const leftParentId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const rightParentId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const leftParent: Node = {
      id: leftParentId,
      type: 'person',
      name: i18n.t('genogram:person.male', { defaultValue: 'Male' }) as string,
      sex: 'male',
      x: leftParentX,
      y: parentY,
      w: size.width,
      h: size.height,
      width: size.width,
      height: size.height,
      category: 'genogram',
    } as Node;

    const rightParent: Node = {
      id: rightParentId,
      type: 'person',
      name: i18n.t('genogram:person.female', { defaultValue: 'Female' }) as string,
      sex: 'female',
      x: rightParentX,
      y: parentY,
      w: size.width,
      h: size.height,
      width: size.width,
      height: size.height,
      category: 'genogram',
    } as Node;

    const marriageEdge: Edge = {
      id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'marriage',
      from: leftParentId,
      to: rightParentId,
      fromAnchor: 'right',
      toAnchor: 'left',
    } as Edge;
    const leftParentChildEdge: Edge = {
      id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'child',
      from: leftParentId,
      to: selectedId,
      fromAnchor: 'bottom',
      toAnchor: 'top',
    } as Edge;
    const rightParentChildEdge: Edge = {
      id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'child',
      from: rightParentId,
      to: selectedId,
      fromAnchor: 'bottom',
      toAnchor: 'top',
    } as Edge;

    const cmds: ICommand[] = [];
    cmds.push(new ApplyGenogramAutoLayoutCommand([leftParent, rightParent], [
      marriageEdge,
      leftParentChildEdge,
      rightParentChildEdge,
    ]));
    cmds.push(new CreateNodeCommand(leftParent));
    cmds.push(new CreateNodeCommand(rightParent));
    cmds.push(
      new CreateEdgeCommand({
        id: marriageEdge.id,
        type: marriageEdge.type,
        from: marriageEdge.from,
        to: marriageEdge.to,
        category: 'genogram',
        fromAnchor: marriageEdge.fromAnchor,
        toAnchor: marriageEdge.toAnchor,
      }),
    );

    cmds.push(
      new CreateEdgeCommand({
        id: leftParentChildEdge.id,
        type: leftParentChildEdge.type,
        from: leftParentChildEdge.from,
        to: leftParentChildEdge.to,
        category: 'genogram',
        fromAnchor: leftParentChildEdge.fromAnchor,
        toAnchor: leftParentChildEdge.toAnchor,
        attrs: { __captionHidden: true },
      }),
    );
    cmds.push(
      new CreateEdgeCommand({
        id: rightParentChildEdge.id,
        type: rightParentChildEdge.type,
        from: rightParentChildEdge.from,
        to: rightParentChildEdge.to,
        category: 'genogram',
        fromAnchor: rightParentChildEdge.fromAnchor,
        toAnchor: rightParentChildEdge.toAnchor,
        attrs: { __captionHidden: true },
      }),
    );

    return new CompositeCommand('Add Parents', cmds);
  }
}
